import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import {
    Bot,
    User,
    Database,
    TrendingUp,
    FileText,
    BookOpen,
    Receipt,
    ShieldAlert,
    Package,
    Users,
    CreditCard,
    ExternalLink,
    X,
    Navigation,
    ArrowRight,
} from 'lucide-react';
import { ChatMessage, AISource } from '../../../types/aiChat';
import './ChatMessageBubble.css';

interface ChatMessageBubbleProps {
    message: ChatMessage;
    onCloseChat?: () => void;
}

interface NavigationAction {
    label: string;
    path: string;
}

const parseNavigationActions = (content: string): { cleanContent: string; actions: NavigationAction[] } => {
    if (!content) return { cleanContent: '', actions: [] };

    const regex = /\[ACTION_NAVIGATE:\s*label="([^"]+)",\s*path="([^"]+)"\]/gi;
    const actions: NavigationAction[] = [];
    let match;

    while ((match = regex.exec(content)) !== null) {
        actions.push({
            label: match[1],
            path: match[2],
        });
    }

    const cleanContent = content.replace(regex, '').trim();

    return { cleanContent, actions };
};

const renderSourceIcon = (iconName?: string) => {
    switch (iconName) {
        case 'trending-up':
            return <TrendingUp size={11} />;
        case 'file-text':
            return <FileText size={11} />;
        case 'book-open':
            return <BookOpen size={11} />;
        case 'receipt':
            return <Receipt size={11} />;
        case 'shield-alert':
            return <ShieldAlert size={11} />;
        case 'package':
            return <Package size={11} />;
        case 'users':
            return <Users size={11} />;
        case 'credit-card':
            return <CreditCard size={11} />;
        default:
            return <Database size={11} />;
    }
};

const getSourceDefaultDetail = (label: string): string => {
    const labelLower = label.toLowerCase();
    if (labelLower.includes('contab') || labelLower.includes('puc')) {
        return 'Información extraída del Plan Único de Cuentas (PUC) y el módulo de contabilidad general de EasyPOS.';
    }
    if (labelLower.includes('vent') || labelLower.includes('métrica')) {
        return 'Métricas consolidadas del reporte de ventas diarias y registros de facturación de tu negocio.';
    }
    if (labelLower.includes('compr') || labelLower.includes('gasto')) {
        return 'Registros de facturas de compra, comprobantes de egreso y gastos operativos registrados.';
    }
    if (labelLower.includes('factur')) {
        return 'Historial de documentos de Facturación Electrónica DIAN expedidos en la plataforma.';
    }
    if (labelLower.includes('tercer') || labelLower.includes('client')) {
        return 'Fichas de información de clientes, proveedores y terceros registrados en la base de datos.';
    }
    return 'Datos e información consultados en tiempo real desde los módulos operativos de EasyPOS.';
};

export const ChatMessageBubble: React.FC<ChatMessageBubbleProps> = ({ message, onCloseChat }) => {
    const isUser = message.role === 'user';
    const navigate = useNavigate();
    const [selectedSource, setSelectedSource] = useState<AISource | null>(null);

    const { cleanContent, actions } = parseNavigationActions(message.content);

    const handleSourceClick = (src: AISource) => {
        setSelectedSource((prev) => (prev?.label === src.label ? null : src));
    };

    const handleNavigateToModule = (src: AISource) => {
        if (onCloseChat) {
            onCloseChat();
        }

        if (src.url) {
            navigate(src.url);
            return;
        }

        const labelLower = src.label.toLowerCase();
        if (labelLower.includes('contab') || labelLower.includes('puc') || labelLower.includes('asiento')) {
            navigate('/accounting');
        } else if (labelLower.includes('vent') || labelLower.includes('métrica')) {
            navigate('/stock');
        } else if (labelLower.includes('compr') || labelLower.includes('gasto')) {
            navigate('/compras');
        } else if (labelLower.includes('factur')) {
            navigate('/invoice');
        } else if (labelLower.includes('tercer') || labelLower.includes('client')) {
            navigate('/terceros');
        } else if (labelLower.includes('cuent') || labelLower.includes('cobrar') || labelLower.includes('pagar')) {
            navigate('/accounts-management');
        } else if (labelLower.includes('inventari') || labelLower.includes('product')) {
            navigate('/stock');
        }
    };

    const handleActionClick = (path: string) => {
        if (onCloseChat) {
            onCloseChat();
        }
        navigate(path);
    };

    return (
        <div className={`chat-bubble-container ${isUser ? 'user' : 'assistant'}`}>
            <div className="chat-bubble-avatar">
                {isUser ? <User size={16} /> : <Bot size={16} />}
            </div>
            <div className="chat-bubble-content">
                <div className="chat-bubble-sender">
                    {isUser ? 'Tú' : 'Asistente IA'}
                </div>
                <div className="chat-bubble-text">
                    <ReactMarkdown>{cleanContent}</ReactMarkdown>
                </div>
                {actions.length > 0 && (
                    <div className="chat-actions-container">
                        {actions.map((action, idx) => (
                            <button
                                key={idx}
                                type="button"
                                className="chat-action-btn"
                                onClick={() => handleActionClick(action.path)}
                            >
                                <Navigation size={13} className="chat-action-icon" />
                                <span>{action.label}</span>
                                <ArrowRight size={13} className="chat-action-arrow" />
                            </button>
                        ))}
                    </div>
                )}
                {!isUser && message.sources && message.sources.length > 0 && (
                    <div className="chat-sources-container">
                        <span className="chat-sources-title">Fuentes consultadas:</span>
                        <div className="chat-sources-chips">
                            {message.sources.map((src, i) => {
                                const isSelected = selectedSource?.label === src.label;
                                return (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => handleSourceClick(src)}
                                        className={`chat-source-chip clickable ${isSelected ? 'active' : ''}`}
                                        title={`Ver detalles de ${src.label}`}
                                    >
                                        {renderSourceIcon(src.icon)}
                                        <span>{src.label}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Popover Card de la fuente seleccionada */}
                        {selectedSource && (
                            <div className="chat-source-popover animate-in">
                                <div className="chat-source-popover-header">
                                    <div className="chat-source-popover-title">
                                        {renderSourceIcon(selectedSource.icon)}
                                        <span>{selectedSource.label}</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="chat-source-popover-close"
                                        onClick={() => setSelectedSource(null)}
                                        title="Cerrar detalle"
                                    >
                                        <X size={13} />
                                    </button>
                                </div>
                                <p className="chat-source-popover-body">
                                    {selectedSource.detail || getSourceDefaultDetail(selectedSource.label)}
                                </p>
                                <div className="chat-source-popover-footer">
                                    <button
                                        type="button"
                                        className="chat-source-popover-link"
                                        onClick={() => handleNavigateToModule(selectedSource)}
                                    >
                                        <span>Ir al módulo completo</span>
                                        <ExternalLink size={12} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

